/**
 * Framework-agnostic SEO head-tag resolution.
 *
 * This module is the single source of truth for turning a
 * (`pageSlug`, `pagePath`, `SeoConfiguration`) triple into a plain,
 * serialisable description of the `<head>` tags a page should carry:
 * resolved title, meta[], link[], and JSON-LD script[].
 *
 * It is consumed by:
 *   - `useSEO` (runtime, via `@unhead/vue`) — see `../composables/useSEO.ts`
 *   - `dcsSeoPlugin`'s build-time static-HTML emitter — see
 *     `../plugins/dcsSeoPlugin.ts`
 *
 * Keeping the resolution here (rather than inside the Vue composable) means
 * the runtime and the build-time emitter produce byte-identical tags from the
 * same `seo.yaml`, with no Vue/unhead dependency required at build time.
 *
 * Runtime behaviour is intentionally identical to the previous in-composable
 * logic **except** for one corrected bug: `og:title` now falls back to the
 * fully-resolved (template-applied) page title instead of the raw, untemplated
 * `page.title`. Previously `og:title` could diverge from the `<title>` element
 * (e.g. `<title>Iron Oak Contractors | Our Services</title>` but
 * `og:title = "Our Services"`).
 */

import type {
  SeoConfiguration,
  GlobalSeoConfig,
  SeoOpenGraphConfig,
  SeoTwitterConfig,
  SeoSchemaConfig,
  ResolvedPageSeo,
} from '../types/seo'
import {
  buildGlobalGraph,
  buildBreadcrumbList,
  buildBlogPosting,
  buildFaqPage,
  graphAbsorbs,
  absolutizeUrl,
  type BreadcrumbCrumb,
  type ReviewSource,
  type FaqSource,
  type BlogMeta,
} from './schemaGraph'

// =============================================================================
// Plain head-tag structures (no framework types)
// =============================================================================

/** A `<meta>` tag — either a `name=`/`content=` or `property=`/`content=` pair. */
export interface HeadMetaTag {
  name?: string
  property?: string
  content: string
}

/** A `<link>` tag (canonical, alternate, etc.). */
export interface HeadLinkTag {
  rel: string
  href: string
  hreflang?: string
}

/** A `<script type="application/ld+json">` tag carrying serialised JSON-LD. */
export interface HeadScriptTag {
  type: string
  /** Pre-serialised JSON-LD string (already `JSON.stringify`-ed). */
  children: string
}

/**
 * The complete, framework-agnostic set of resolved `<head>` tags for a page.
 *
 * - `title` is the final, template-applied title (what goes in `<title>`).
 * - `meta` covers description, keywords, robots, verification, OG, Twitter.
 * - `link` covers canonical + hreflang alternates.
 * - `script` covers JSON-LD (global + page schemas).
 * - `jsonLd` is the same JSON-LD as parsed objects, for callers that want the
 *   structured form (e.g. `useSEO().getSchema()`).
 */
export interface ResolvedHeadTags {
  title: string
  meta: HeadMetaTag[]
  link: HeadLinkTag[]
  script: HeadScriptTag[]
  jsonLd: object[]
  /** The fully-resolved page SEO (merged global + page) used to build tags. */
  resolved: ResolvedPageSeo
}

/** Optional overrides applied on top of the resolved config when building tags. */
export interface HeadTagOverrides {
  /** Override the resolved `<title>`. */
  title?: string
  /**
   * Page-specific title fallback used when `seo.yaml` has no `title` for this
   * page (e.g. the route `title` from `pages.yaml`). Unlike `global.defaultTitle`
   * this IS run through `titleTemplate`, so un-configured routes (blog posts,
   * etc.) get unique titles rather than the global default.
   */
  fallbackTitle?: string
  /** Override the resolved meta description. */
  description?: string
  /** Override the meta keywords value. */
  keywords?: string
  /**
   * Force the robots directive (e.g. `'noindex, nofollow'`). When supplied this
   * wins over both page- and global-level robots.
   */
  robots?: string
  /** Replace the JSON-LD schema objects entirely (already-built objects). */
  schemas?: object[]
  /** Extra meta tags appended after the generated ones. */
  meta?: HeadMetaTag[]
  /**
   * Emit a `<meta name="keywords">` tag from the page's `keywords` field.
   *
   * Defaults to `false` so the `useSEO` runtime path stays byte-identical to
   * its historical output (which never emitted keywords). The static-HTML
   * emitter opts in (`true`) to surface page keywords in the baked `<head>`.
   */
  includeKeywords?: boolean

  // ── Schema-completeness inputs (additive; OFF unless supplied) ─────────────
  // The build-time static-HTML emitter opts into these to bake the cross-linked
  // @graph spine + BreadcrumbList + (honest) BlogPosting/FAQPage/Review. The
  // runtime `useSEO` composable does NOT pass them, so its byte output is
  // unchanged. See `./schemaGraph.ts`.

  /**
   * Emit the global `@graph` spine (Organization + WebSite + the promoted
   * LocalBusiness node, cross-linked by `@id`) PREPENDED before the per-schema
   * JSON-LD. When true, the LocalBusiness subtype already present in
   * `global.schemas` is ABSORBED into the graph (not emitted a second time).
   * Default `false`.
   */
  emitGraph?: boolean
  /**
   * Ordered Home → … → current breadcrumb trail (absolute item URLs). When it
   * has more than one hop a `BreadcrumbList` is emitted; the home page (≤ 1 hop)
   * emits none.
   */
  breadcrumbTrail?: BreadcrumbCrumb[]
  /**
   * REAL review items (from `.dcs/content.yaml`) for the LocalBusiness node.
   * Honest Review[] + aggregateRating are emitted ONLY for items with a numeric
   * rating + non-empty text + authorName; empty/missing ⇒ nothing.
   */
  reviews?: ReviewSource[]
  /**
   * Free-text trade/occupational license (from the `business.license`
   * content.yaml key). When non-empty a `hasCredential`
   * (`EducationalOccupationalCredential`, `credentialCategory: "license"`) is
   * added to the LocalBusiness node in the `@graph`; empty/missing ⇒ nothing.
   * Only consulted when `emitGraph` is true.
   */
  license?: string
  /**
   * Structured FAQ pairs (frontmatter `faq:` / `.dcs/faq.yaml`). A `FAQPage` is
   * emitted ONLY when at least one entry has a non-empty question AND answer.
   */
  faq?: FaqSource[]
  /**
   * Blog-post metadata. When `headline` is present a `BlogPosting` is emitted —
   * UNLESS the page's own schemas already hand-author a `BlogPosting` (then the
   * builder defers to the authored copy to avoid duplication).
   */
  blogMeta?: BlogMeta
}

// =============================================================================
// Open Graph / Twitter / JSON-LD generation
// =============================================================================

/**
 * Generate Open Graph meta tags from config.
 *
 * @param resolvedTitle - the final, template-applied page title. Used as the
 *   `og:title` fallback so OG stays consistent with `<title>`.
 */
export function generateOpenGraphMeta(
  og: SeoOpenGraphConfig,
  global: GlobalSeoConfig,
  resolvedTitle: string,
  pageDescription: string,
  canonical: string
): Array<{ property: string; content: string }> {
  const tags: Array<{ property: string; content: string }> = []

  // og:title falls back to the *resolved* (templated) title — see module
  // docblock. `og.title` is already pre-resolved in `resolvePageSeo` to
  // `page.openGraph.title || resolvedTitle`, so this is consistent either way.
  tags.push({ property: 'og:title', content: og.title || resolvedTitle })
  tags.push({ property: 'og:description', content: og.description || pageDescription })
  // og:url is absolutized: an already-absolute og.url is used verbatim, a
  // site-relative og.url is joined onto the site URL, and a missing og.url falls
  // back to the (already-absolute) canonical. This stops a relative per-page
  // `openGraph.url` leaking a non-absolute og:url into the baked <head>.
  tags.push({ property: 'og:url', content: absolutizeUrl(og.url, global.siteUrl, canonical) })
  tags.push({ property: 'og:type', content: og.type || 'website' })

  const image = og.image || global.images?.ogDefault
  if (image) {
    tags.push({ property: 'og:image', content: image })
    if (og.imageAlt || resolvedTitle) {
      tags.push({ property: 'og:image:alt', content: og.imageAlt || resolvedTitle })
    }
    if (og.imageWidth) {
      tags.push({ property: 'og:image:width', content: String(og.imageWidth) })
    }
    if (og.imageHeight) {
      tags.push({ property: 'og:image:height', content: String(og.imageHeight) })
    }
  }

  if (global.siteName) {
    tags.push({ property: 'og:site_name', content: global.siteName })
  }

  if (global.locale) {
    tags.push({ property: 'og:locale', content: global.locale })
  }

  // Article-specific tags
  if (og.type === 'article') {
    if (og.publishedTime) {
      tags.push({ property: 'article:published_time', content: og.publishedTime })
    }
    if (og.modifiedTime) {
      tags.push({ property: 'article:modified_time', content: og.modifiedTime })
    }
    if (og.author) {
      tags.push({ property: 'article:author', content: og.author })
    }
    if (og.section) {
      tags.push({ property: 'article:section', content: og.section })
    }
    if (og.tags) {
      og.tags.forEach((tag) => {
        tags.push({ property: 'article:tag', content: tag })
      })
    }
  }

  return tags
}

/**
 * Generate Twitter Card meta tags from config.
 *
 * @param resolvedTitle - the final, template-applied page title (Twitter title
 *   fallback), mirroring the OG behaviour.
 */
export function generateTwitterMeta(
  twitter: SeoTwitterConfig,
  global: GlobalSeoConfig,
  resolvedTitle: string,
  pageDescription: string
): Array<{ name: string; content: string }> {
  const tags: Array<{ name: string; content: string }> = []

  tags.push({ name: 'twitter:card', content: twitter.card || 'summary_large_image' })
  tags.push({ name: 'twitter:title', content: twitter.title || resolvedTitle })
  tags.push({ name: 'twitter:description', content: twitter.description || pageDescription })

  const image = twitter.image || global.images?.twitterDefault
  if (image) {
    tags.push({ name: 'twitter:image', content: image })
    if (twitter.imageAlt || resolvedTitle) {
      tags.push({ name: 'twitter:image:alt', content: twitter.imageAlt || resolvedTitle })
    }
  }

  const site = twitter.site || global.social?.twitter
  if (site) {
    tags.push({ name: 'twitter:site', content: site.startsWith('@') ? site : `@${site}` })
  }

  if (twitter.creator) {
    tags.push({
      name: 'twitter:creator',
      content: twitter.creator.startsWith('@') ? twitter.creator : `@${twitter.creator}`,
    })
  }

  return tags
}

/**
 * Generate JSON-LD schema objects from schema configs, auto-populating common
 * WebSite properties from global config.
 */
export function generateJsonLd(schemas: SeoSchemaConfig[], global: GlobalSeoConfig): object[] {
  return schemas.map((schema) => {
    const base: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': schema.type,
    }

    // Merge properties
    if (schema.properties) {
      Object.assign(base, schema.properties)
    }

    // Auto-populate common properties from global config
    if (schema.type === 'WebSite' && global.siteUrl && !base.url) {
      base.url = global.siteUrl
    }
    if (schema.type === 'WebSite' && global.siteName && !base.name) {
      base.name = global.siteName
    }

    return base
  })
}

/**
 * Resolve page SEO by merging global defaults with page-specific config.
 *
 * Behavioural changes from the historical in-composable version (both bug
 * fixes):
 *   1. `openGraph.title` falls back to the **template-applied** page title, not
 *      the raw `page.title`, so `og:title` matches `<title>`.
 *   2. The `titleTemplate` is applied **only** to a page-specific title
 *      (`page.title`, or the `fallbackTitle` arg). It is no longer applied to
 *      `global.defaultTitle`, which is already the complete brand title —
 *      templating it produced `"Brand | Default Title | Brand"` doubling on any
 *      page without its own `seo.yaml` entry.
 *
 * @param fallbackTitle - a page-specific title to use when `seo.yaml` has no
 *   `title` for this page (e.g. the route `title` from `pages.yaml`). It IS run
 *   through `titleTemplate`; `global.defaultTitle` is the last resort and is not.
 */
export function resolvePageSeo(
  pageSlug: string,
  pagePath: string | undefined,
  seoConfig: SeoConfiguration | undefined,
  fallbackTitle?: string
): ResolvedPageSeo {
  const global = seoConfig?.global ?? {}
  const page = seoConfig?.pages?.[pageSlug] ?? {}

  // Build canonical URL
  let canonical = page.canonical || ''
  if (!canonical && global.siteUrl) {
    const path = pagePath ?? (pageSlug === 'home' ? '/' : `/${pageSlug}`)
    canonical = `${global.siteUrl.replace(/\/$/, '')}${path}`
  }

  // Build title. The `%s` slot is for a PAGE-SPECIFIC title only (a seo.yaml
  // `page.title` or a `pages.yaml` route title). `global.defaultTitle` is
  // already brand-complete, so it is used verbatim — never re-templated.
  const pageSpecificTitle = page.title || fallbackTitle
  let title: string
  if (pageSpecificTitle) {
    title =
      page.noTitleTemplate || !global.titleTemplate
        ? pageSpecificTitle
        : global.titleTemplate.replace('%s', pageSpecificTitle)
  } else {
    title = global.defaultTitle || pageSlug
  }

  // Merge Open Graph. og:title now falls back to the *resolved* (templated)
  // title rather than the raw page.title, keeping it consistent with <title>.
  const openGraph: ResolvedPageSeo['openGraph'] = {
    type: page.openGraph?.type || 'website',
    title: page.openGraph?.title || title,
    description: page.openGraph?.description || page.description || global.defaultDescription || '',
    ...page.openGraph,
  }

  // Merge Twitter
  const twitter: ResolvedPageSeo['twitter'] = {
    card: page.twitter?.card || 'summary_large_image',
    ...page.twitter,
  }

  // Combine schemas (global + page)
  const schemas = [...(global.schemas ?? []), ...(page.schemas ?? [])]

  return {
    title,
    description: page.description || global.defaultDescription || '',
    canonical,
    robots: page.robots || global.robots || 'index, follow',
    openGraph,
    twitter,
    schemas,
    alternates: page.alternates ?? [],
    // Surface keywords on the resolved object so both runtime and emitter can
    // emit the meta tag without re-reading the raw page config.
    keywords: page.keywords,
  }
}

// =============================================================================
// Unified head-tag builder (consumed by runtime + emitter)
// =============================================================================

/**
 * Build the complete, framework-agnostic set of `<head>` tags for a page.
 *
 * This is the function both the `useSEO` runtime and the build-time emitter
 * call, guaranteeing identical output. Pass `overrides` to mirror the
 * composable's `applyHead(overrides)` behaviour, or to force `robots` (used by
 * the emitter's `noindex` option).
 */
export function buildHeadTags(
  pageSlug: string,
  pagePath: string | undefined,
  seoConfig: SeoConfiguration | undefined,
  overrides?: HeadTagOverrides
): ResolvedHeadTags {
  const resolved = resolvePageSeo(pageSlug, pagePath, seoConfig, overrides?.fallbackTitle)
  const global = seoConfig?.global ?? {}

  const title = overrides?.title ?? resolved.title
  const description = overrides?.description ?? resolved.description
  const robots = overrides?.robots ?? resolved.robots
  const keywords = overrides?.keywords ?? resolved.keywords

  // ── meta ────────────────────────────────────────────────────────────────
  const meta: HeadMetaTag[] = []

  meta.push({ name: 'description', content: description })

  // Keywords are opt-in (default off) so the runtime composable output is
  // unchanged; the static-HTML emitter passes includeKeywords: true.
  if (keywords && overrides?.includeKeywords) {
    meta.push({ name: 'keywords', content: keywords })
  }

  if (robots) {
    meta.push({ name: 'robots', content: robots })
  }

  // Verification codes
  if (global.verification?.google) {
    meta.push({ name: 'google-site-verification', content: global.verification.google })
  }
  if (global.verification?.bing) {
    meta.push({ name: 'msvalidate.01', content: global.verification.bing })
  }

  // Open Graph
  const ogMeta = generateOpenGraphMeta(resolved.openGraph, global, title, description, resolved.canonical)
  meta.push(...ogMeta.map((t) => ({ property: t.property, content: t.content })))

  // Twitter
  const twitterMeta = generateTwitterMeta(resolved.twitter, global, title, description)
  meta.push(...twitterMeta.map((t) => ({ name: t.name, content: t.content })))

  // Caller-supplied extra meta
  if (overrides?.meta) {
    meta.push(...overrides.meta)
  }

  // ── link ──────────────────────────────────────────────────────────────
  const link: HeadLinkTag[] = []

  if (resolved.canonical) {
    // Guarantee the canonical is absolute even if a page set a relative one.
    link.push({ rel: 'canonical', href: absolutizeUrl(resolved.canonical, global.siteUrl, resolved.canonical) })
  }

  resolved.alternates.forEach((alt) => {
    link.push({ rel: 'alternate', href: alt.href, hreflang: alt.hreflang })
  })

  // ── script (JSON-LD) ──────────────────────────────────────────────────
  const jsonLd = buildJsonLd(resolved, global, overrides)
  const script: HeadScriptTag[] = jsonLd.map((schema) => ({
    type: 'application/ld+json',
    children: JSON.stringify(schema),
  }))

  return { title, meta, link, script, jsonLd, resolved }
}

/**
 * Assemble the JSON-LD objects for a page, weaving in the schema-completeness
 * builders (`./schemaGraph.ts`) ONLY when the caller opts in via `overrides`.
 *
 * Order (when opted in): `@graph` spine → per-page/global schemas →
 * `BreadcrumbList` → `BlogPosting` → `FAQPage`. With no opt-in inputs this is
 * exactly the historical `overrides.schemas ?? generateJsonLd(...)`, so the
 * runtime composable's bytes are unchanged.
 *
 * Anti-duplication:
 *   • When `emitGraph` absorbs the LocalBusiness subtype into the spine, that
 *     subtype is excluded from the per-schema emission (no second copy).
 *   • A builder `BlogPosting` is skipped when the page already hand-authors one.
 */
function buildJsonLd(
  resolved: ResolvedPageSeo,
  global: GlobalSeoConfig,
  overrides?: HeadTagOverrides
): object[] {
  // A full schema replacement still wins (back-compat with applyHead overrides).
  if (overrides?.schemas) return overrides.schemas

  const out: object[] = []

  const emitGraph = overrides?.emitGraph === true

  // Per-schema config to emit normally. When the graph absorbs the LocalBusiness
  // subtype, drop it here so it is not emitted twice.
  let perSchema: SeoSchemaConfig[] = resolved.schemas
  if (emitGraph) {
    const graph = buildGlobalGraph(global, {
      reviews: overrides?.reviews,
      license: overrides?.license,
    })
    if (graph.length > 0) {
      out.push(...graph)
      // Remove the schemas the @graph now provides canonically — the
      // LocalBusiness subtype it folded in, plus any global Organization/WebSite
      // (the graph emits cross-linked ones) — to avoid duplicate nodes.
      perSchema = resolved.schemas.filter((s) => !graphAbsorbs(s, global))
    }
  }

  out.push(...generateJsonLd(perSchema, global))

  // BreadcrumbList from the supplied trail (home ⇒ none).
  if (overrides?.breadcrumbTrail) {
    out.push(...buildBreadcrumbList(overrides.breadcrumbTrail))
  }

  // BlogPosting — only when the page does not already hand-author one.
  if (overrides?.blogMeta && global.siteUrl) {
    const alreadyAuthored = resolved.schemas.some((s) => s.type === 'BlogPosting')
    if (!alreadyAuthored) {
      out.push(...buildBlogPosting(overrides.blogMeta, global.siteUrl, global))
    }
  }

  // FAQPage — honesty-gated (only real structured Q&A).
  if (overrides?.faq) {
    out.push(...buildFaqPage(overrides.faq))
  }

  return out
}
